/**
 * Records what the API actually answered when a booking was refused.
 *
 * @author Milton Filho
 */
import * as Sentry from '@sentry/react';

export type BookingPath = 'online' | 'queue-replay';

interface Props {
  /** The rejection as it arrived, classified here rather than at the call site. */
  error?: unknown;
  path: BookingPath;
  /** Only meaningful on a replay: whether this item had failed before. */
  hadFailedBefore?: boolean;
}

/**
 * Exists to settle a question nobody can answer by waiting.
 *
 * The backend's `409` for a duplicate booking is deployed in production and has
 *   never fired. That is ambiguous in a way that matters: it reads the same
 *   whether no duplicate was ever attempted, or one was and the API answered
 *   something else. Nothing on our side distinguished them, because a refused
 *   booking produced a toast and a `bookAppointmentFailure` carrying the status
 *   into redux, where it was read by nothing and shipped nowhere.
 *
 * Forcing the answer is not an option: provoking a duplicate means creating a
 *   real appointment, and the backend confirmed on 2026-09-08 that Clinicient
 *   has no sandbox — `BASE_URL` is a single hardcoded value and `IS_PRODUCTION`
 *   counts staging as production, so a staging booking creates a real
 *   appointment. (This paragraph used to argue it from the staging S3 leak.
 *   That leak was closed on 2026-09-03 and the argument was stale before it was
 *   written; the conclusion happens to be right for a different reason.)
 *
 * So this makes the *first real occurrence* answer it, with nobody watching:
 *
 *   - a `409` recorded → the backend's path works
 *   - a `500` recorded → a conflict happened and the `409` did not fire
 *   - `no-status` recorded → the API refused in a shape carrying no status, so
 *     the question stays open and the reason is now visible rather than absent
 *   - nothing ever recorded → **no refusal reached this code.** Not "no
 *     duplicate was attempted", which is what this list used to claim
 *
 * That last row is the correction, and it mattered. Two other things produce
 *   silence, and reading silence as "nothing happened" is exactly the mistake
 *   an instrument is supposed to prevent:
 *
 *   - a network failure, deliberately excluded below
 *   - a `200` carrying `result: 'fail'`. `handleAjaxSuccess` throws a bare
 *     `Error` for that, with no `response` and no status, and it sits *above*
 *     the `tap` that calls this — so the refusal used to be dropped by the
 *     guard here. Whether this API ever answers a booking that way is unknown;
 *     the point is that it can no longer be silent if it does
 *
 * Network failures are still not reported. They say nothing about what the API
 *   decided, and they are the one failure that is already loud: the connection
 *   toast. They are told apart by the request having gone out with no response
 *   coming back, which is what an axios network error looks like.
 *
 * Status and booleans only — no patient, no slot, nothing about who.
 */
/**
 * What the API's answer was, as far as this refusal can tell.
 *
 * `network` is the only one thrown away, so it is the only one that has to be
 *   identified positively rather than by elimination.
 */
const getRefusalStatus = (error: unknown): string | undefined => {
  const candidate = error as
    | { response?: { status?: number }; request?: unknown }
    | undefined;

  const status = candidate?.response?.status;
  if (typeof status === 'number') return String(status);

  // The request went out and nothing came back: a network failure, not a
  //   decision. `response` absent with `request` present is axios's shape for
  //   it, and it is the only case dropped.
  if (candidate?.request) return undefined;

  // Refused, but in a shape that carries no status — a bare `Error` thrown out
  //   of `handleAjaxSuccess` for a `200` with `result: 'fail'`, for instance.
  //   Recorded, because the alternative is silence that reads as "no refusal".
  return 'no-status';
};

export const reportBookingRefusal = ({
  error,
  path,
  hadFailedBefore,
}: Props): void => {
  const status = getRefusalStatus(error);
  if (status === undefined) return;

  Sentry.captureMessage('Booking refused by the API', {
    level: 'info',
    tags: {
      area: 'booking-refusal',
      status,
      path,
    },
    ...(hadFailedBefore === undefined ? {} : { extra: { hadFailedBefore } }),
  });
};
